import { CurrencyAmount } from '@kodiak-finance/sdk-core'
import { ButtonError } from 'components/Button'
import Column, { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import Step from 'components/StepLine/common/Step'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { BAULT_ROUTER_ADDRESSES } from 'constants/addresses'
import { POOL_TO_INFRARED_VAULT } from 'constants/lists'
import { useToken } from 'hooks/Tokens'
import { useBaultDatas } from 'hooks/useBault'
import { useConnection } from 'hooks/useConnection'
import { useKodiakFarmByPool } from 'hooks/useKodiakFarm'
import { useRewardVaultBasicInfo } from 'hooks/useRewardVault'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import { useUSDValue } from 'hooks/useUSDValue'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useMemo, useState } from 'react'
import { ArrowRight, ArrowUpRight, Zap } from 'react-feather'
import styled from 'styled-components'

import { IslandContextType } from '../context/IslandContextType'
import AutoCompoundDets from './components/AutoCompoundDets'
import { useOnBaultDeposit, useOnStake, useOnVaultStake } from './hooks/useOnStakeCallback'
import { usePreviewBaultShares } from './hooks/usePreviewBaultShares'
import BaultDepositStep from './steps/BaultDepositStep'
import RewardVaultStakeStep from './steps/RewardVaultStakeStep'
import { useSelectLockTimeStep } from './steps/SelectLockTimeStep'
import StakeStep from './steps/StakeStep'

const TokenSelectorWrapper = styled.div`
  background: ${({ theme }) => theme.cardPrimary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`

const TokenSelectorTitle = styled.h3`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
`

const TokenOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TokenOption = styled.label<{ active: boolean; disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ active, theme, disabled }) =>
      disabled ? theme.backgroundSurface : active ? theme.deprecated_primary1 : theme.cardBorderSecondary};
  background: ${({ active, theme, disabled }) =>
    disabled ? `${theme.backgroundScrim}80` : active ? `${theme.deprecated_primary1}15` : theme.cardPrimary};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${({ active, theme, disabled }) =>
      disabled ? theme.backgroundInteractive : active ? theme.deprecated_primary1 : theme.borderSecondary};
  }
`

const TokenOptionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const TokenBalanceDisplay = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.deprecated_text2};
  background: ${({ theme }) => theme.backgroundModule};
  padding: 6px 12px;
  border-radius: 6px;
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
`

const TokenDescription = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.deprecated_text3};
  margin-top: 8px;
`

const InfoCard = styled.div`
  // background: ${({ theme }) => `${theme}15`};
  // border: 1px solid ${({ theme }) => `${theme}30`};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`

const InfoCardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`

const InfoCardDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.deprecated_text2};
  margin-bottom: 12px;
`

const TokenFlowCard = styled.div`
  padding: 12px;
  background: ${({ theme }) => `${theme.backgroundScrim}80`};
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`

const TokenIconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.backgroundModule};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`

const TokenFlowContent = styled.div`
  flex-grow: 1;
`

const TokenFlowRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`

const StrategyDisplay = styled.div`
  padding: 12px;
  background: ${({ theme }) => `${theme.backgroundScrim}80`};
  border-radius: 8px;
`

const StrategyRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`

const StrategyTotal = styled(StrategyRow)`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.backgroundModule};
  color: ${({ theme }) => theme.deprecated_text1};
  font-weight: 500;
`

const InfraredLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: white;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  padding: 12px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    rgba(218, 60, 120, 0.9) 0%,
    rgba(218, 83, 82, 0.9) 50%,
    rgba(237, 128, 56, 0.9) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.07);
  transition: all 0.15s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background: linear-gradient(90deg, rgba(226, 68, 128, 1) 0%, rgba(229, 94, 93, 1) 50%, rgba(245, 136, 64, 1) 100%);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }
`
const StyledArrow = styled(ArrowUpRight)`
  width: 16px;
  height: 16px;
  margin-left: 10px;
  color: rgba(255, 255, 255, 0.9);
  transition: transform 0.15s ease;

  ${InfraredLink}:hover & {
    transform: translateX(2px) translateY(-2px);
  }
`

const InfraredLinkWrapper = styled.div<{ disabled: boolean }>`
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`

const StakeView = ({
  islandId: poolAddress,
  rewardVault,
  rewardVaultRewardToken,
  routerAddress,
  stakingData: { stakingType, kodiakFarmAddress, baultAddress, rewardVaultAddress, baultFarmAddress },
  showMigrationOption,
  poolApiResponse,
}: IslandContextType) => {
  const [value, setValue] = useState('')
  const [useAutoCompound, setUseAutoCompound] = useState(true)
  const [stakingTokenType, setStakingTokenType] = useState('lp') // 'lp' or 'bault'
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol
  const rewardVaultRewardsLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} rewards` : 'rewards'
  const rewardVaultAprLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} APR` : 'Reward APR'

  const token = useToken(poolAddress)
  const { account, chainId } = useConnection()
  const balance = useTokenBalance(account, token || undefined, REFETCH_POLICY_FAST)

  // Add balance for bault tokens
  const baultToken = useToken(baultAddress)
  const baultBalance = useTokenBalance(account, baultToken || undefined, REFETCH_POLICY_FAST)

  const kodiakFarm = useKodiakFarmByPool(kodiakFarmAddress)
  const rewardVaultContractAddress = stakingType === 'rewardVault' ? rewardVault : undefined
  const baultContractAddress = useAutoCompound ? baultAddress : undefined
  const baultRouterContractAddress = BAULT_ROUTER_ADDRESSES[chainId]

  const { data } = useBaultDatas(useAutoCompound ? baultAddress : undefined)
  const { data: rewardVaultData } = useRewardVaultBasicInfo(rewardVault)

  const currencyPriceData = useGetTokenPrice(poolAddress)
  const currencyUSDValue = useUSDValue(value, currencyPriceData.price)

  const effectiveStakingType = useMemo(() => {
    if (stakingTokenType === 'bault' && stakingType === 'rewardVault') {
      return 'baultToKodiak'
    }

    if (stakingType === 'rewardVault' && useAutoCompound && Boolean(baultAddress) && stakingTokenType === 'lp') {
      return 'bault'
    }

    return stakingType
  }, [stakingType, useAutoCompound, stakingTokenType, baultAddress])

  const lockTimeSelectStep = useSelectLockTimeStep(stakingType === 'kodiakFarm' ? kodiakFarmAddress : undefined, value)

  const typedValue = useMemo(() => {
    const relevantToken = stakingTokenType === 'bault' ? baultToken : token
    return tryParseCurrencyAmount(value, relevantToken || undefined)
  }, [value, token, baultToken, stakingTokenType])

  const isError = useMemo(() => {
    const relevantBalance = stakingTokenType === 'bault' ? baultBalance : balance
    return typedValue && relevantBalance?.lessThan(typedValue)
  }, [typedValue, balance, baultBalance, stakingTokenType])

  const shouldFetchShares = effectiveStakingType === 'bault' && !!baultContractAddress && stakingTokenType === 'lp'
  const amountForPreview = useMemo(() => {
    if (typedValue) return typedValue
    // When no input, return zero amount like DepositView does
    if (token && shouldFetchShares) {
      return CurrencyAmount.fromRawAmount(token, '0')
    }
    return undefined
  }, [typedValue, token, shouldFetchShares])

  const { expectedShares, isLoading, vaultSymbol } = usePreviewBaultShares(
    baultContractAddress,
    amountForPreview,
    shouldFetchShares
  )
  const showTokenTypeSelector = stakingType === 'rewardVault' && !!baultAddress
  // const isBaultKodiakAvailable = !!baultFarmAddress
  // const hasBaultBalance = baultBalance && !baultBalance.equalTo('0')
  // const farmSupportsBaultStaking = Boolean(baultAddress)

  const showAutoCompound = stakingType === 'rewardVault' && baultAddress && stakingTokenType === 'lp'

  // const showExpectedShares = Boolean(
  //   useAutoCompound && (isLoading || (expectedShares && typedValue && !typedValue.equalTo('0')))
  // )

  const approveStep = useApproveStep(
    typedValue,
    effectiveStakingType === 'baultToKodiak'
      ? kodiakFarm?.address
      : stakingType === 'kodiakFarm'
      ? kodiakFarm?.address
      : effectiveStakingType === 'bault'
      ? baultRouterContractAddress
      : rewardVault
  )

  // Farm staking callback
  const {
    onStake: onFarmStake,
    attemptingTxnStake: attemptingTxnFarmStake,
    txHashStake: txHashFarmStake,
    clear: clearFarmStake,
  } = useOnStake(typedValue, kodiakFarm?.address)

  // Vault staking callback
  const {
    onStake: onVaultStake,
    attemptingTxnStake: attemptingTxnVaultStake,
    txHashStake: txHashVaultStake,
    clear: clearVaultStake,
  } = useOnVaultStake(typedValue, rewardVaultContractAddress)

  const {
    onStake: onBaultDeposit,
    attemptingTxnStake: attemptingTxnBaultDeposit,
    txHashStake: txHashBaultDeposit,
    clear: clearBaultDeposit,
  } = useOnBaultDeposit(typedValue, baultRouterContractAddress, baultContractAddress, account)

  // const {
  //   onStake: onBaultToKodiakStake,
  //   attemptingTxnStake: attemptingTxnBaultToKodiakStake,
  //   txHashStake: txHashBaultToKodiakStake,
  //   clear: clearBaultToKodiakStake,
  // } = useOnBaultToKodiakStake(typedValue, farmContract, baultToken)

  const onStake = useMemo(() => {
    if (effectiveStakingType === 'baultToKodiak') {
      return onFarmStake
    }
    if (effectiveStakingType === 'kodiakFarm') {
      return onFarmStake
    }
    if (effectiveStakingType === 'bault') {
      return onBaultDeposit
    }
    return onVaultStake
  }, [effectiveStakingType, onFarmStake, onVaultStake, onBaultDeposit])

  const attemptingTxnStake = useMemo(() => {
    if (effectiveStakingType === 'baultToKodiak' || effectiveStakingType === 'kodiakFarm') {
      return attemptingTxnFarmStake
    }
    if (effectiveStakingType === 'bault') {
      return attemptingTxnBaultDeposit
    }
    return attemptingTxnVaultStake
  }, [effectiveStakingType, attemptingTxnFarmStake, attemptingTxnVaultStake, attemptingTxnBaultDeposit])

  const txHashStake = useMemo(() => {
    if (effectiveStakingType === 'baultToKodiak' || effectiveStakingType === 'kodiakFarm') {
      return txHashFarmStake
    }
    if (effectiveStakingType === 'bault') {
      return txHashBaultDeposit
    }
    return txHashVaultStake
  }, [effectiveStakingType, txHashFarmStake, txHashVaultStake, txHashBaultDeposit])

  const clear = useCallback(() => {
    if (effectiveStakingType === 'baultToKodiak' || effectiveStakingType === 'kodiakFarm') {
      clearFarmStake()
    } else if (effectiveStakingType === 'bault') {
      clearBaultDeposit()
    } else {
      clearVaultStake()
    }
  }, [effectiveStakingType, clearFarmStake, clearVaultStake, clearBaultDeposit])

  const onFinish = () => {
    clear()
    setIsConfirmationOpen(false)
    setValue('')
  }

  // Farm stake step (with lock time)
  const farmStakeStep = useStepWithNext('StakeStep', StakeStep, {
    amount: typedValue,
    onStake,
    hash: txHashStake,
    attemptingTxn: attemptingTxnStake,
    pendingText: (
      <>
        Stake {typedValue?.toSignificant(6)} {typedValue?.currency.symbol}
      </>
    ),
    transactionWaiting: true,
  })

  // First, implement the isInfraRedVault logic
  const isInfraRedVault = useMemo(() => {
    if (!chainId || !poolAddress) return false

    const chainMapping = POOL_TO_INFRARED_VAULT[chainId.toString()]
    if (!chainMapping) return false

    const vaultPath = chainMapping[poolAddress.toLowerCase()]
    return !!vaultPath
  }, [chainId, poolAddress])

  // Create a memo for the vault URL
  const infraredVaultUrl = useMemo(() => {
    if (!isInfraRedVault || !chainId || !poolAddress) return null

    const vaultPath = POOL_TO_INFRARED_VAULT[chainId.toString()]?.[poolAddress.toLowerCase()]
    if (!vaultPath) return null

    return `https://infrared.finance/vaults/${vaultPath}`
  }, [isInfraRedVault, chainId, poolAddress])

  // RewardVault stake step (no lock time, different APR display)
  const rewardVaultStakeStep = useStepWithNext('RewardVaultStakeStep', RewardVaultStakeStep, {
    amount: typedValue,
    onStake,
    hash: txHashStake,
    attemptingTxn: attemptingTxnStake,
    rewardVault,
    rewardVaultRewardSymbol,
    apr: poolApiResponse?.farm?.apr,
    pendingText: (
      <>
        Stake {typedValue?.toSignificant(6)} {typedValue?.currency.symbol} in Reward Vault
      </>
    ),
    transactionWaiting: true,
  })

  const baultStakeStep = useStepWithNext('BaultStakeStep', BaultDepositStep, {
    amount: typedValue,
    onStake,
    hash: txHashStake,
    attemptingTxn: attemptingTxnStake,
    baultAddress,
    apr: 0,
    pendingText: (
      <>
        Deposit {typedValue?.toSignificant(6)} {typedValue?.currency.symbol} into Auto-Compounding Vault
      </>
    ),
    transactionWaiting: true,
  })

  const stakeStep = useMemo(() => {
    if (effectiveStakingType === 'baultToKodiak') {
      return farmStakeStep
    }
    if (effectiveStakingType === 'kodiakFarm') {
      return farmStakeStep
    }
    if (effectiveStakingType === 'bault') {
      return baultStakeStep
    }
    return rewardVaultStakeStep
  }, [effectiveStakingType, farmStakeStep, baultStakeStep, rewardVaultStakeStep])

  const steps: Step[] = useMemo(() => {
    const baseSteps = [approveStep]

    if (stakingType === 'kodiakFarm' || effectiveStakingType === 'baultToKodiak') {
      baseSteps.push(lockTimeSelectStep)
    }

    baseSteps.push(stakeStep)
    return baseSteps
  }, [approveStep, stakingType, effectiveStakingType, lockTimeSelectStep, stakeStep])

  const buttonText = useMemo(() => {
    if (isError) return 'Insufficient balance'
    if (!value || parseFloat(value) === 0) return 'Enter amount to stake'

    if (effectiveStakingType === 'baultToKodiak') {
      return `Stake in Kodiak Farm`
    }

    if (stakingType === 'kodiakFarm') {
      return `Stake in Kodiak Farm`
    }

    if (effectiveStakingType === 'bault') {
      return `Deposit to Auto-Compound`
    }

    return `Stake in Reward Vault`
  }, [isError, stakingType, effectiveStakingType, value])

  return (
    <>
      <StepLineModal
        isOpen={isConfirmationOpen}
        onDismiss={onFinish}
        onFinish={onFinish}
        header={<>Stake</>}
        steps={steps}
      />
      <AutoColumn gap="12px">
        {showTokenTypeSelector && (
          <TokenSelectorWrapper>
            <TokenSelectorTitle>Choose Token to Stake</TokenSelectorTitle>
            <TokenOptionsContainer>
              <TokenOption active={stakingTokenType === 'lp'} onClick={() => setStakingTokenType('lp')}>
                <TokenOptionHeader>
                  <input
                    type="radio"
                    name="token-type"
                    checked={stakingTokenType === 'lp'}
                    onChange={() => setStakingTokenType('lp')}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Island LP Tokens</span>
                </TokenOptionHeader>

                <TokenBalanceDisplay>
                  <span>Balance:</span>
                  <span>
                    {balance?.toSignificant(6) || '0'} {rewardVaultData.stakeToken?.symbol}
                  </span>
                </TokenBalanceDisplay>

                <TokenDescription>Stake in Reward Vault or enable auto-compounding</TokenDescription>
              </TokenOption>

              {/* <TokenOption
                active={stakingTokenType === 'bault'}
                disabled={!isBaultKodiakAvailable}
                onClick={() => isBaultKodiakAvailable && setStakingTokenType('bault')}
              >
                <TokenOptionHeader>
                  <input
                    type="radio"
                    name="token-type"
                    checked={stakingTokenType === 'bault'}
                    onChange={() => isBaultKodiakAvailable && setStakingTokenType('bault')}
                    style={{ marginRight: '8px' }}
                    disabled={!isBaultKodiakAvailable}
                  />
                  <span>Bault Tokens</span>
                </TokenOptionHeader>

                <TokenBalanceDisplay>
                  <span>Balance:</span>
                  <span>
                    {baultBalance?.toSignificant(6) || '0'} {data?.baultShareToken.symbol}
                  </span>
                </TokenBalanceDisplay>

                <TokenDescription>
                  {isBaultKodiakAvailable
                    ? `Stake your auto-compound tokens in Kodiak Farm for additional rewards
                    : 'No Kodiak Farm available for staking these tokens'}
                </TokenDescription>
              </TokenOption> */}
            </TokenOptionsContainer>
          </TokenSelectorWrapper>
        )}

        <CurrencyInputPanel
          value={value}
          onUserInput={setValue}
          locked={showMigrationOption}
          lockedMessage={`To earn ${rewardVaultRewardsLabel}, first migrate your assets from Kodiak Farm to this Reward Vault`}
          onMax={() => {
            const relevantBalance = stakingTokenType === 'bault' ? baultBalance : balance
            setValue(relevantBalance?.toExact() || '')
          }}
          showMaxButton={true}
          currency={stakingTokenType === 'bault' ? baultToken : token}
          id="add-liquidity-input-tokena"
          showCommonBases
          apiUSDValue={currencyUSDValue}
        />

        {/* Auto-Compound Toggle (only for LP tokens in Reward Vault context) */}
        {showAutoCompound && (
          <AutoCompoundDets
            isActive={useAutoCompound}
            baultAddress={baultAddress}
            onToggle={() => setUseAutoCompound((prev) => !prev)}
            currency={token}
            expectedShares={expectedShares}
            isLoading={isLoading}
            vaultSymbol={vaultSymbol}
            rewardVaultRewardSymbol={rewardVaultRewardSymbol}
            apy={data?.apy}
          />
        )}

        {/* Special info card for Bault tokens staking in Kodiak Farm */}
        {stakingTokenType === 'bault' && (
          <InfoCard>
            <InfoCardHeader>
              {/* <Lock size={16} style={{ color: 'var(--purple-400)', marginRight: '8px' }} /> */}
              <h3 style={{ color: 'var(--white)', fontWeight: '500' }}>Staking Auto-Compound Tokens</h3>
            </InfoCardHeader>

            <InfoCardDescription>
              You're staking your bault tokens in Kodiak Farm to earn additional {rewardVaultRewardsLabel} while your
              original LP tokens continue to auto-compound.
            </InfoCardDescription>

            <TokenFlowCard>
              <TokenIconContainer>
                <Zap size={20} style={{ color: 'var(--blue-400)' }} />
              </TokenIconContainer>
              <TokenFlowContent>
                <TokenFlowRow>
                  <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginRight: '4px' }}>
                    Your bault tokens:
                  </span>
                  <ArrowRight size={12} style={{ color: 'var(--gray-500)', margin: '0 4px' }} />
                  <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>Auto-compounding at</span>
                  <span style={{ fontSize: '12px', color: 'var(--green-400)', marginLeft: '4px' }}>
                    {data?.apy || '11.2'}% APY
                  </span>
                </TokenFlowRow>
                <TokenFlowRow>
                  <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginRight: '4px' }}>
                    Plus when staked:
                  </span>
                  <ArrowRight size={12} style={{ color: 'var(--gray-500)', margin: '0 4px' }} />
                  <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>Additional</span>
                  <span style={{ fontSize: '12px', color: 'var(--purple-400)', marginLeft: '4px' }}>
                    +{kodiakFarm?.apr || '0'}% {rewardVaultAprLabel}
                  </span>
                </TokenFlowRow>
              </TokenFlowContent>
            </TokenFlowCard>

            <StrategyDisplay>
              <StrategyRow>
                <span style={{ color: 'var(--gray-400)' }}>Auto-Compound APY:</span>
                <span style={{ color: 'var(--green-400)' }}>{data?.apy || '11.2'}%</span>
              </StrategyRow>
              <StrategyRow>
                <span style={{ color: 'var(--gray-400)' }}>Additional Kodiak Farm APR:</span>
                <span style={{ color: 'var(--purple-400)' }}>+{kodiakFarm?.apr || '5.2'}%</span>
              </StrategyRow>
              <StrategyTotal>
                <span>Combined Effective Rate:</span>
                <span>~{((Number(data?.apy) || 11.2) + (Number(kodiakFarm?.apr) || 5.2)).toFixed(1)}%</span>
              </StrategyTotal>
            </StrategyDisplay>
          </InfoCard>
        )}
      </AutoColumn>

      <Column style={{ gap: '8px' }}>
        <ButtonError
          onClick={() => setIsConfirmationOpen(true)}
          disabled={isError || !value || parseFloat(value) === 0}
          error={isError}
        >
          {buttonText}
        </ButtonError>
        {isInfraRedVault && infraredVaultUrl && (
          <InfraredLinkWrapper disabled={!value || parseFloat(value) === 0}>
            <InfraredLink href={infraredVaultUrl} target="_blank" rel="noopener noreferrer">
              Stake with Infrared to earn iBGT
              <StyledArrow />
            </InfraredLink>
          </InfraredLinkWrapper>
        )}
      </Column>
    </>
  )
}

export default StakeView
